import { Draft } from 'immer';
import { calcFutureValue, isNullOrUndefined } from '@shared';
import {
  calculateMonthsBetweenYears,
  ReturnOnInvestmentCalculations,
} from './calcFundingSourceDerivatives';
import { analyzeInvestmentReturns } from '@shared/calcProjectedROI';

function calcNetFutureValueOfExistingAssets(
  selfFunding: SelfFundingSource,
  yearsTillLtc: number,
) {
  const { existingAssetsValue, annualRateOfReturn, gainsTaxRate } = selfFunding;

  selfFunding.futureValueOfInitialInvestment =
    isNullOrUndefined(existingAssetsValue) ||
    isNullOrUndefined(annualRateOfReturn)
      ? 0
      : calcFutureValue(existingAssetsValue, annualRateOfReturn, yearsTillLtc);

  const gainsTaxLiabilityOnExistingAssets: number =
    calcCapitalGainsTaxLiability(
      existingAssetsValue,
      gainsTaxRate,
      selfFunding.futureValueOfInitialInvestment,
    );
  return (
    selfFunding.futureValueOfInitialInvestment -
    gainsTaxLiabilityOnExistingAssets
  );
}

function calcNetFutureValueOfMonthlyContributions(
  selfFunding: SelfFundingSource,
) {
  const { monthlyContributionAmount, annualRateOfReturn, gainsTaxRate } =
    selfFunding;

  const futureValueOfMonthlyContributions: number = isNullOrUndefined(
    monthlyContributionAmount,
  )
    ? 0
    : calcFutureValueOfMonthlyContributions(
        monthlyContributionAmount,
        annualRateOfReturn ?? 0,
        selfFunding.contributionMonthCount,
      );

  const gainsTaxLiabilityOnfMonthlyContributions: number =
    calcCapitalGainsTaxLiability(
      (monthlyContributionAmount ?? 0) * selfFunding.contributionMonthCount,
      gainsTaxRate,
      futureValueOfMonthlyContributions,
    );

  return (
    futureValueOfMonthlyContributions - gainsTaxLiabilityOnfMonthlyContributions
  );
}

export function calcSelfFundingProjectedCostCoverage(
  mutableClient: Draft<Client>,
) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: {
        contributionStartYear,
        contributionEndYear,
        monthlyIncome,
      },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    appliedInferenceSet,
  } = mutableClient;

  // calc net future value of initial investment
  selfFunding.contributionYearCount =
    contributionEndYear - contributionStartYear;
  selfFunding.contributionMonthCount =
    selfFunding.contributionYearCount > 0
      ? calculateMonthsBetweenYears(contributionStartYear, contributionEndYear)
      : 1;

  const netFutureValueOfInitialInvestment = calcNetFutureValueOfExistingAssets(
    selfFunding,
    appliedInferenceSet.yearsTillLtc,
  );
  // calc net future value of monthly contributions
  const netFutureValueOfMonthlyContributions: number =
    calcNetFutureValueOfMonthlyContributions(selfFunding);

  // calc total value of monthly income
  selfFunding.monthlyIncomeTotal =
    allPhaseCareMonthsNeeded * (monthlyIncome ?? 0);

  // combine all values to get total value
  selfFunding.selfFundingTotalValue =
    netFutureValueOfInitialInvestment +
    netFutureValueOfMonthlyContributions +
    selfFunding.monthlyIncomeTotal;

  selfFunding.selfFundingProjectedCostCoverage =
    selfFunding.selfFundingTotalValue;
}

function calcCapitalGainsTaxLiability(
  principal: number | null,
  gainsTaxRate: number | null,
  investmentFutureValue: number,
) {
  if (isNullOrUndefined(principal) || isNullOrUndefined(gainsTaxRate)) {
    return 0;
  }
  return (investmentFutureValue - principal) * gainsTaxRate;
}

function calcFutureValueOfMonthlyContributions(
  monthlyContributionAmount: number,
  annualRateOfReturn: number,
  contributionMonthCount: number,
): number {
  if (
    isNullOrUndefined(monthlyContributionAmount) ||
    isNullOrUndefined(annualRateOfReturn) ||
    isNullOrUndefined(contributionMonthCount)
  ) {
    return 0;
  }
  if (annualRateOfReturn === 0) {
    return monthlyContributionAmount * contributionMonthCount;
  }

  return (
    monthlyContributionAmount *
    ((Math.pow(1 + annualRateOfReturn / 12, contributionMonthCount) - 1) /
      (annualRateOfReturn / 12))
  );
}

export function calcSelfFundingTotalsOnClaim(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: { monthlyIncome, existingAssetsValue },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = mutableClient;

  selfFunding.monthlyIncomeTotal =
    allPhaseCareMonthsNeeded * (monthlyIncome ?? 0);

  // total value is equal to sum of existing assets and monthly income
  selfFunding.selfFundingTotalValue =
    (existingAssetsValue ?? 0) + selfFunding.monthlyIncomeTotal;

  // set total cost and coverage equal to toal value
  selfFunding.selfFundingTotalCost = selfFunding.selfFundingTotalValue;
  selfFunding.selfFundingProjectedCostCoverage =
    selfFunding.selfFundingTotalValue;

  // assume that all funds can be used for LTC, so this is zero.
  selfFunding.selfFundingNonLtcPayout = 0;
}

export function calcSelfFundingTotalCost(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: {
        existingAssetsValue,
        monthlyContributionAmount,
        monthlyIncomeTotal,
        contributionMonthCount,
      },
    },
  } = mutableClient;
  if (
    isNullOrUndefined(existingAssetsValue) &&
    isNullOrUndefined(monthlyContributionAmount) &&
    isNullOrUndefined(monthlyIncomeTotal)
  ) {
    selfFunding.selfFundingTotalCost = undefined;
    return;
  }

  selfFunding.selfFundingTotalCost =
    (monthlyContributionAmount ?? 0) * contributionMonthCount +
    (existingAssetsValue ?? 0) +
    (monthlyIncomeTotal ?? 0);
}

export function calcSelfFundingProjectedROI(
  mutableClient: Draft<Client>,
  discountRate?: number,
): ReturnOnInvestmentCalculations {
  const {
    fundingSources: {
      selfFunding: { selfFundingTotalValue, selfFundingTotalCost, contributionStartYear },
      selfFunding,
    },
  } = mutableClient;

  if (isNullOrUndefined(selfFundingTotalValue) || !selfFundingTotalCost) {
    return {
      investments: [],
      returns: [],
      investmentPeriodYears: 0,
    };
  }

  // assume that all funds can be used for LTC, so this is zero.
  selfFunding.selfFundingNonLtcPayout = 0;

  const returnOnInvestment =
    (selfFundingTotalValue - selfFundingTotalCost) / selfFundingTotalCost;
  selfFunding.selfFundingProjectedReturnOnInvestment = returnOnInvestment;

  const investmentCashFlows = convertInvestmentCashFlows(mutableClient);
  const returnCashFlows = convertReturnCashFlows(
    mutableClient,
    selfFundingTotalValue,
  );

  // Using the contribution start year as the base year for IRR calculation
  // This ensures each self-funding source has its own timeline
  const baseYear = contributionStartYear ?? new Date().getFullYear();

  const { cagr, internalRateOfReturn, yearsToLastReturn } = analyzeInvestmentReturns(
    investmentCashFlows,
    returnCashFlows,
    discountRate,
    baseYear,
  );

  selfFunding.selfFundingProjectedCompoundAnnualGrowthRate = cagr;
  selfFunding.selfFundingProjectedInternalRateOfReturn = internalRateOfReturn;
  selfFunding.investmentPeriodYears = yearsToLastReturn;
  selfFunding.investmentCashFlows = investmentCashFlows;
  selfFunding.returnCashFlows = returnCashFlows;
  return {
    investments: investmentCashFlows,
    returns: returnCashFlows,
    investmentPeriodYears: yearsToLastReturn,
  };
}

function convertInvestmentCashFlows(mutableClient: Draft<Client>): CashFlow[] {
  const {
    fundingSources: { selfFunding },
  } = mutableClient;
  const {
    existingAssetsValue,
    monthlyContributionAmount,
    contributionMonthCount,
  } = selfFunding;

  const cashFlows: CashFlow[] = [];

  // Add initial investment as year 0
  if (!isNullOrUndefined(existingAssetsValue)) {
    cashFlows.push({
      year: 0,
      amount: existingAssetsValue,
    });
  }

  if (!isNullOrUndefined(monthlyContributionAmount)) {
    // Get full years and remaining months
    const fullYears = Math.floor(contributionMonthCount / 12) ?? 0;
    const remainingMonths = contributionMonthCount % 12;

    // Add full year contributions
    for (let year = 0; year < fullYears; year++) {
      cashFlows.push({
        year: year,
        amount: monthlyContributionAmount * 12,
      });
    }

    // Add partial year contribution if there are remaining months
    if (remainingMonths > 0) {
      cashFlows.push({
        year: fullYears,
        amount: monthlyContributionAmount * remainingMonths,
      });
    }
  }

  return cashFlows;
}

function convertReturnCashFlows(
  mutableClient: Draft<Client>,
  selfFundingTotalValue: number,
): CashFlow[] {
  const {
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    fundingSources: {
      selfFunding: { monthlyIncomeTotal, contributionStartYear, contributionEndYear },
    },
    appliedInferenceSet: { ltcAtYear },
  } = mutableClient;

  const returnYears = Math.ceil(allPhaseCareMonthsNeeded / 12) ?? 1;
  const yearlyAmount = returnYears
    ? (selfFundingTotalValue - (monthlyIncomeTotal ?? 0)) / returnYears
    : 0;

  // Calculate the year offset for when returns start (care start year)
  // Returns should start when care starts, not during the contribution period
  const careStartYear = ltcAtYear ?? contributionEndYear;
  const yearsUntilCareStarts = careStartYear - contributionStartYear;

  return Array.from({ length: returnYears }, (_, index) => ({
    year: yearsUntilCareStarts + index,
    amount: yearlyAmount,
  }));
}
